/**
 * Signup pass-through window for the EnsureUserProfile recovery guard.
 *
 * This is the complement to useUserProfile's `fromCache` guard. That guard stops
 * a cold-reload cache-miss from looking 'missing'; this window stops a FRESH
 * SIGNUP from looking 'missing'. On signup, EnsureUserProfile subscribes the
 * instant Auth reports the user — before the awaited `ensure_user_profile`
 * callable has finished committing `users/{uid}` — so the server itself briefly
 * returns a (server-confirmed) non-existence that the fromCache guard does NOT
 * suppress. Recovery is redundant there (the signup's own awaited call is
 * creating the doc), so we suppress it for accounts created within the window
 * and let the listener catch up. Outside the window, a missing profile is a
 * genuinely interrupted or legacy signup worth recovering.
 */
export const RECOVERY_SUPPRESS_WINDOW_MS = 30_000;

/**
 * True if `creationTime` (a Firebase `User.metadata.creationTime` RFC-2822
 * string) is within the pass-through window relative to `nowMs`. An absent or
 * unparseable value returns false — failing toward attempting recovery, the
 * safer default for a genuinely-missing profile.
 */
export function isWithinSignupWindow(
  creationTime: string | undefined,
  nowMs: number,
): boolean {
  if (!creationTime) return false;
  const createdMs = Date.parse(creationTime);
  if (!Number.isFinite(createdMs)) return false;
  return nowMs - createdMs < RECOVERY_SUPPRESS_WINDOW_MS;
}
